import {
  getConversationID,
  getCountryCode,
  getCurrencyCode,
  getLanguageCode,
  getLanguageCode3Char,
  getLocale,
  isMyMscArea,
  isPartnership,
} from '../utilFuncs/contextAwareUtils';
import { CookieName, cookieValue, getEcid } from '../genericUtils/cookieUtils';
import { getActiveSession } from '../session/SessionsUtils';

export class mulesoftEntity {
  MSC_Conversation_ID!: string;
  MSC_Locale!: string;
  MSC_Country_Code!: string;
  MSC_Language_Code!: string;
  MSC_Currency_Code!: string;
  MSC_ECID?: string;
  bearerToken: string | undefined;

  constructor(
    conversationID: string,
    locale: string,
    countryCode: string,
    languageCode: string,
    currencyCode: string,
    ecid: string | undefined,
    bearerToken: string | undefined
  ) {
    this.MSC_Conversation_ID = conversationID;
    this.MSC_Locale = locale;
    this.MSC_Country_Code = countryCode;
    this.MSC_Language_Code = languageCode;
    this.MSC_Currency_Code = currencyCode;
    this.MSC_ECID = ecid;
    this.bearerToken = bearerToken;
  }

  toHeaders(): {
    'MSC-Locale': string;
    'MSC-Language-Code': string;
    'MSC-Currency-Code': string;
    'MSC-Conversation-ID': string;
    'MSC-Country-Code': string;
    'MSC-ECID'?: string;
  } {
    const headers: {
      'MSC-Locale': string;
      'MSC-Language-Code': string;
      'MSC-Currency-Code': string;
      'MSC-Conversation-ID': string;
      'MSC-Country-Code': string;
      'MSC-ECID'?: string;
      Authorization?: string;
    } = {
      'MSC-Conversation-ID': this.MSC_Conversation_ID,
      'MSC-Locale': this.MSC_Locale,
      'MSC-Country-Code': this.MSC_Country_Code,
      'MSC-Language-Code': this.MSC_Language_Code,
      'MSC-Currency-Code': this.MSC_Currency_Code,
    };

    if (this.MSC_ECID !== undefined) {
      headers['MSC-ECID'] = this.MSC_ECID;
    }

    if (this.bearerToken !== undefined) {
      headers['Authorization'] = `Bearer ${this.bearerToken}`;
    }

    return headers;
  }
}

/**
 * Builds a header entity to pass to the API.
 *
 * @returns {mulesoftEntity} The constructed mulesoftEntity object with the necessary headers.
 */
export function buildHeaderEntity(
  isNewVersionApi = false,
  isNeedsMulesoftJWT = false
): mulesoftEntity {
  const currencyCode = getCurrencyCode() || '';
  const conversationID = getConversationID() || '';
  const locale = getLocale() || '';
  const languageCode = isNewVersionApi ? getLanguageCode3Char() : getLanguageCode() || '';
  const countryCode = getCountryCode() || '';
  const ecidCookieValue = getEcidCookieValue();
  let bearerToken = undefined;

  if (isPartnership()) {
    bearerToken = isNeedsMulesoftJWT
      ? getActiveSession().token
      : getBearerTokenAzureB2CPartnership();
  } else if (isMyMscArea()) {
    bearerToken = getActiveSession().token;
  }

  // Build header entity to pass to the API
  return new mulesoftEntity(
    conversationID,
    locale,
    countryCode,
    languageCode,
    currencyCode,
    ecidCookieValue,
    bearerToken
  );
}

/**
 * Retrieves the booking base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "bookingBaseApi".
 */
export function getBookingBaseAPI(): string {
  const bookingBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="bookingBaseApi"]'
  ) as HTMLMetaElement;
  return bookingBaseApiMetaTag.content;
}

/**
 * Retrieves the private area base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "privateAreaBaseApi".
 */
export function getPrivateAreaBaseAPI(): string {
  const privateAreaBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="privateAreaBaseApi"]'
  ) as HTMLMetaElement;
  return privateAreaBaseApiMetaTag?.content;
}

/**
 * Retrieves the customer base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "customerBaseApi".
 */
export function getCustomerBaseAPI(): string {
  const customerBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="customerBaseApi"]'
  ) as HTMLMetaElement;
  return customerBaseApiMetaTag.content;
}

/**
 * Retrieves the customer base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "customerBaseApi".
 */
export function getEcidCookieValue(): string {
  return getEcid();
}

/**
 * Retrieves the Management User Token base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "managementUserTokenBaseAPI".
 */
export function getManagementUserTokenBaseAPI(): string {
  const managementUserTokenBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="managementUserTokenBaseAPI"]'
  ) as HTMLMetaElement;
  return managementUserTokenBaseApiMetaTag.content;
}

/**
 * Retrieves the Bearer Token from the cookie of azure Partnership
 *
 * @returns {string | undefined} The Bearer Token if an active session is found, otherwise undefined.
 */
export const getBearerTokenAzureB2CPartnership = (): string | undefined => {
  const cookieNameFromMeta: HTMLMetaElement = document.querySelector(
    'meta[name="cookieName"]'
  ) as HTMLMetaElement;
  let cookieName: string = CookieName.PARTNERSHIP_AZURE_B2C;
  if (cookieNameFromMeta && cookieNameFromMeta.content) {
    cookieName = cookieNameFromMeta.content;
  }
  return cookieValue(cookieName);
};

/**
 * Retrieves the shared service base API URL from a meta tag in the document.
 *
 * @returns {string} The content of the meta tag with the name "sharedServiceBaseAPI".
 */
export function getSharedServiceAreaBaseAPI(): string {
  const sharedServiceBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="sharedServiceBaseApi"]'
  ) as HTMLMetaElement;
  return sharedServiceBaseApiMetaTag?.content;
}

/**
 * Base URL for GET /customer/dataByLogin (services-* host, e.g. https://services-qa.msccruises.com).
 * Separate from sharedServiceBaseApi (API gateway EAPI).
 */
export function getCustomerDataByLoginBaseAPI(): string {
  const meta: HTMLMetaElement = document.querySelector(
    'meta[name="customerDataByLoginBaseApi"]'
  ) as HTMLMetaElement;
  return meta?.content ?? '';
}
